import useOverlay, {OverlayState} from "@/hooks/common/useOverlay";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {FlagGoodsBrandCd, FlagGoodsCd, FlagGoodsTmember, FlagGoodsTp} from "@COMPONENTS/common/flag/Desktop";
import SharePopup from "@COMPONENTS/common/modal/share/Desktop";
import {WithCalendarRef} from "@COMPONENTS/hoc/withCalendar";
import {D_EXCLUDE_KEYS} from "@COMPONENTS/product/common/popup/common/constant";
import EventList from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventList";
import {
	BannerContents,
	BannerYellowLive,
	BannerYellowPangDeal,
	BannerYellowPick,
} from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListBanner";
import EventListCalendar from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListCalendar";
import EventListFilters from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListFIlters";
import useFilterStore from "@COMPONENTS/product/pages/localList/common/filterHook";
import * as style from "@COMPONENTS/style/productlist/departurepopup";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {IconButton} from "@FRAMEWORK/ui/desktop/button";
import {StarIcon} from "@FRAMEWORK/ui/desktop/flag";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import {StackList, StackListItem} from "@FRAMEWORK/ui/desktop/list";
import {PopupContainer} from "@FRAMEWORK/ui/desktop/popup";
import {RedText} from "@FRAMEWORK/ui/desktop/text";
import useGlobalGoodsLive from "@QUERIES/bff/global/useGlobalGoodsLive";
import useBannerContents from "@QUERIES/essential/banner/useBannerContents";
import useBannerGoodsDeal from "@QUERIES/essential/banner/useBannerGoodsDeal";
import useGoodsBannerPick from "@QUERIES/portal/useGoodsBannerPick";
import useEventByGoodsMonth, {EventByGoodsMonth} from "@QUERIES/prdt/event/useEventByMonth";
import {GoodsByMenu} from "@QUERIES/prdt/goods/useGoodsByMenu";
import useGoodsDetail, {GoodsDetail} from "@QUERIES/prdt/goods/useGoodsDetail";
import {GoodsAvailEvent} from "@QUERIES/prdt/goods/useGoodsOneAvailEvent";
import useGoodsOption from "@QUERIES/prdt/goods/useGoodsOption";
import {format} from "@yb/utils";
import dayjs, {Dayjs} from "dayjs";
import {useRouter} from "next/router";
import {useEffect, useMemo, useRef, useState} from "react";

interface Paramters {
	goodsCd?: string;
	availEvent?: GoodsAvailEvent;
	month?: string;
	goods?: GoodsDetail;
	product?: GoodsByMenu;
}

interface EventExt extends EventByGoodsMonth {
	sumPrice: number;
	isEvReserve: string;
	isEvConfirm: string;
	evReserveStatus: string;
}

interface FilterType {
	key: string;
	text: string;
	value: string;
	asc?: boolean;
}

interface SortType {
	text: string;
	prop: string;
	asc: boolean;
}

const EventInfo = (goods: GoodsDetail) => {
	const router = useRouter();
	const dspSid = `${router.query?.dspSid?.slice(0, -2)}00` as string;
	const menu = router.query?.menu as string;

	const bannerContentsFetch = useBannerContents("GOST"); // WEB_CONT_DIVI_CD 출발일보기
	const bannerContents = bannerContentsFetch.data?.body;

	const bannerDealFetch = useBannerGoodsDeal(goods.goodsCd);
	const bannerDeal = bannerDealFetch.data?.body;

	const bannerLiveFetch = useGlobalGoodsLive(goods.goodsCd);
	const bannerLive = bannerLiveFetch.data?.body;

	const bannerPickFetch = useGoodsBannerPick(goods.goodsCd);
	const bannerPick = bannerPickFetch.data?.body;

	// 공유하기
	const [open] = useOverlay();
	const handleShare = () => {
		open(prop => (
			<SharePopup
				{...prop}
				baseUrl={`${publicRuntimeConfig.ENV_PRODUCT_URL}/product/localList`}
				parameter={`menu=${menu}&dspSid=${dspSid}&goodsCd=${goods.goodsCd}`}
			/>
		));
	};
	return (
		<style.ContentTop>
			<style.SummaryInfo justify="space-between" align="start">
				<style.TagSquareWrap gap="5">
					<FlagGoodsCd goodsCd={goods.goodsCd} />
					<FlagGoodsTp goodsTp={goods.goodsTp} />
					<FlagGoodsBrandCd goodsBrandCd={goods.goodsBrandCd} />
					<FlagGoodsTmember goodsTmember={goods.goodsTmember} />
				</style.TagSquareWrap>
				<StackList>
					<StackListItem>
						<Flex align="center">
							<StarIcon />
							<style.Grade>{goods.rvwAvgScore > 0 ? goods.rvwAvgScore : `-`}</style.Grade>/ 5
						</Flex>
					</StackListItem>
					<StackListItem>
						<IconButton icon="share" isActive={false} onClick={handleShare}>
							<span>공유하기</span>
						</IconButton>
					</StackListItem>
				</StackList>
			</style.SummaryInfo>
			<style.TopTitle>{goods.goodsNm}</style.TopTitle>
			<style.TopPriceBox>
				<Flex gap="2" align="center">
					최저가
					<RedText>
						<em>{format.koKRFormatNumber(goods.minPrice)}</em> 원
					</RedText>
					({dayjs(goods.minPriceDate).format("YYYY.MM.DD")})
				</Flex>
				<Flex gap="2" align="center">
					최고가
					<RedText>
						<em>{format.koKRFormatNumber(goods.maxPrice)}</em> 원
					</RedText>
					({dayjs(goods.maxPriceDate).format("YYYY.MM.DD")})
				</Flex>
			</style.TopPriceBox>

			{/* 배너 */}
			{bannerContents && <BannerContents contents={bannerContents} />}
			{bannerPick && <BannerYellowPick pick={bannerPick} />}
			{bannerDeal && <BannerYellowPangDeal deal={bannerDeal} />}
			{bannerLive && <BannerYellowLive live={bannerLive} />}
		</style.ContentTop>
	);
};

const EventSort = (props: {sort: {text: string; prop: string; asc: boolean}; handleSort: (e: any) => void}) => {
	const {sort, handleSort} = props;
	const Rating = [
		{text: "출발일순", prop: "outStartDt", asc: true},
		{text: "낮은가격순", prop: "sumPrice", asc: true},
		{text: "높은가격순", prop: "sumPrice", asc: false},
	];
	return (
		<style.ScheduleTitle>
			<h5>일정 선택</h5>
			<StackList cursor={"pointer"}>
				{Rating.map((item, index) => (
					<StackListItem
						key={index}
						data-prop={item.prop}
						data-asc={item.asc}
						data-text={item.text}
						isActive={sort?.text === item.text}
						onClick={handleSort}
					>
						{item.text}
					</StackListItem>
				))}
			</StackList>
		</style.ScheduleTitle>
	);
};

const EventContentsComponent = (props: Paramters & {onClose: () => void}) => {
	const {getFilter} = useFilterStore();
	const [filters, setFilters] = useState<Array<FilterType>>([]);
	const router = useRouter();

	const {goods, availEvent, onClose} = props;

	const {goodsCd, webDisplayCls, minPrice} = goods;
	const dspSid = `${router.query?.dspSid?.slice(0, -2)}00` as string;

	const optionFetch = useGoodsOption(goodsCd, {staleTime: 1000 * 60});
	const option = optionFetch.data?.body;

	const calendarRef = useRef<WithCalendarRef>(null);
	/** 행사리스트 필터링에 사용 (YYYYMMDD) */
	const [currentDate, setCurrentDate] = useState(null);
	/** 달력 세팅에 사용 (YYYYMM) */
	const [currentMonth, setCurrentMonth] = useState(null);

	/** 최소 날짜, 최대 날짜, 이벤트 날짜 세팅 */
	const [minDate, maxDate] = useMemo(() => {
		if (!availEvent) return [null, null];
		const {minOutStartDt, maxOutStartDt} = availEvent.eventMonthList;

		setCurrentMonth(dayjs(availEvent.evStartDt).date(1).format("YYYYMM"));
		return [minOutStartDt, maxOutStartDt];
	}, [availEvent]);

	/** 필터링 된 날짜 세팅 */
	useEffect(() => {
		if (!filters.length) return;
		const dateFilter = filters.find(v => v.key === "outStartDt");
		const monthFilter = filters.find(v => v.key === "outStartMonth");

		const newDate = dateFilter ? dateFilter.value : null;
		const newMonth = monthFilter ? monthFilter.value : null;

		newMonth && setCurrentMonth(newMonth);
		newDate && setCurrentMonth(dayjs(newDate).format("YYYYMM"));
		newDate && setCurrentDate(newDate);
	}, [filters]);

	/** 달력 동기화 */
	useEffect(() => {
		calendarRef.current?.monthChange(currentMonth);
	}, [currentMonth]);

	/** 달 변경 콜백 */
	const onMonthChange = (day: Dayjs) => {
		const newMonth = day ? day.format("YYYYMM") : null;
		setCurrentMonth(newMonth);
	};

	/** 날짜 변경 콜백 */
	const onDateChange = (day: Dayjs) => {
		const newDate = day ? day.format("YYYYMMDD") : null;
		setCurrentDate(newDate);
	};

	const eventByGoodsMonthFetch = useEventByGoodsMonth(dspSid, goodsCd, currentMonth, {
		enabled: !!currentMonth && !optionFetch.isLoading,
		staleTime: 1000 * 60,
		select(data: any) {
			const eventData = data.body;
			eventData?.map(event => {
				const {evProgDivi, avlilBlockQty, adtPrice, airTaxAdtPrice, bafAdtPrice} = event;
				event.sumPrice = adtPrice + airTaxAdtPrice + bafAdtPrice;
				event.isEvReserve = "false"; // 예약가능 여부
				event.isEvConfirm = "false"; // 출발확정 여부
				if (
					evProgDivi !== "05" &&
					((evProgDivi !== "00" && avlilBlockQty > 0) || evProgDivi === "00" || avlilBlockQty <= 0)
				) {
					event.isEvReserve = "true";
				}
				if (evProgDivi === "07" || evProgDivi === "11") {
					event.isEvConfirm = "true";
				}
				if (evProgDivi === "05") {
					event.evReserveStatus = "end";
				} else if (evProgDivi === "00" || avlilBlockQty <= 0) {
					event.evReserveStatus = "wait";
				} else {
					event.evReserveStatus = "can";
				}
				return event.sumPrice;
			});
			return {minPrice, list: eventData};
		},
	});
	const orgEventList = eventByGoodsMonthFetch.data;

	// 정렬 sort
	const [sort, setSort] = useState<SortType>({text: "출발일순", prop: "outStartDt", asc: true});
	const handleSort = e => {
		const {text, prop, asc} = e.target.dataset;
		setSort({text, prop, asc: asc === "true"});
	};

	// 필터 초기화
	useEffect(() => {
		if (!option) return;
		const currentFilter = getFilter()?.filter(v => v.key !== "sort") || [];
		let result = [...currentFilter.filter(v => v.key !== "airCd")];

		// 날짜 데이터 세팅
		const outStartDateList = currentFilter.find(v => v.key === "outStartDateList");
		if (outStartDateList) {
			if (outStartDateList.value.length === 6) {
				result.push({key: "outStartMonth", text: outStartDateList.text, value: outStartDateList.value});
			} else if (outStartDateList.value.length === 8) {
				result.push({key: "outStartDt", text: outStartDateList.text, value: outStartDateList.value});
			}
			result = result.filter(v => v.key !== "outStartDateList");
		}

		const {accommodationList, trafficList} = option;
		// 옵션
		if (accommodationList?.length > 0 && webDisplayCls === "B") {
			const accom = accommodationList.map(item => ({
				key: "accomSeq",
				value: String(item.accomSeq),
				text: item.accomNm,
			}));
			result.push(...accom);
		}
		// 교통편
		if (currentFilter.find(v => v.key === "airCd")) {
			// 리스트에 선택된것만 세팅
			const airCdArray = currentFilter.filter(v => v.key === "airCd")?.map(i => i.value);
			const traffic = trafficList
				.filter(item => airCdArray.includes(item.trCompanySnm))
				.map(item => ({
					key: "trCompanySnm",
					value: item.trCompanySnm,
					text: item.trCompanyNm,
				}));
			result.push(...traffic);
		} else {
			const traffic = trafficList.map(item => ({
				key: "trCompanySnm",
				value: item.trCompanySnm,
				text: item.trCompanyNm,
			}));
			result.push(...traffic);
		}
		setFilters(result);
	}, [option]);

	/** 필터 된 리스트 */
	const filteredList = useMemo(() => {
		if (!orgEventList) return;
		const {minPrice, list} = orgEventList as {minPrice: number; list: Array<EventExt>};
		let copyArray = list;
		filters?.forEach(item => {
			const filterKey = item.key;
			if (D_EXCLUDE_KEYS.indexOf(filterKey) > -1) return;
			copyArray = copyArray?.reduce((acc, event) => {
				const filterKeyList = filters.filter(v => v.key === filterKey);
				filterKeyList.some(filter => {
					const {key, value} = filter;
					if (key === "minPrice") return Number(event.sumPrice) >= Number(value);
					if (key === "maxPrice") return Number(event.sumPrice) <= Number(value);
					return event[key] && event[key].indexOf(value) > -1;
					// eslint-disable-next-line no-param-reassign
				}) && (acc = acc.concat(event));
				return acc;
			}, []);
		});
		return {minPrice, eventList: copyArray || []};
	}, [filters, orgEventList, currentMonth]);

	return (
		<style.ContentBottom>
			<style.Schedule>
				{/* 달력 */}
				<style.FixedScheduleWrap>
					{currentMonth && (
						<EventListCalendar
							value={currentMonth}
							data={filteredList}
							maxDate={maxDate}
							minDate={minDate}
							onDateChange={onDateChange}
							onMonthChange={onMonthChange}
							ref={calendarRef}
						/>
					)}
				</style.FixedScheduleWrap>

				<style.ScheduleWrap>
					{/* sort */}
					<EventSort sort={sort} handleSort={e => handleSort(e)} />
					<style.ScrollHeightBox>
						<div>
							{/* filters */}
							<EventListFilters filters={filters} setFilters={setFilters} option={option} goods={goods} />

							<style.PromotionWrap>
								{/* list */}
								{filteredList && (
									<EventList
										data={filteredList.eventList}
										currentDate={currentDate}
										sort={sort}
										filters={filters}
										setFilters={setFilters}
										onClose={onClose}
									/>
								)}

								<style.CalendarControl>
									<style.CalendarButton
										type="button"
										month="prev"
										onClick={() => calendarRef.current.prevMonth()}
										disabled={!calendarRef.current?.isPrevious}
									>
										<em>이전달</em>
									</style.CalendarButton>
									<span>{dayjs(calendarRef.current?.month).format("YYYY.MM")}</span>
									<style.CalendarButton
										type="button"
										month="next"
										onClick={() => calendarRef.current.nextMonth()}
										disabled={!calendarRef.current?.isNext}
									>
										<em>다음달</em>
									</style.CalendarButton>
								</style.CalendarControl>
							</style.PromotionWrap>
						</div>
					</style.ScrollHeightBox>
				</style.ScheduleWrap>
			</style.Schedule>
		</style.ContentBottom>
	);
};

const EventContents = (props: Paramters & {onClose: () => void}) => (
	<ErrorBoundary logMsg="EventListPop EventContents Error">
		<EventContentsComponent {...props} />
	</ErrorBoundary>
);

const EventListPopComponent = (props: OverlayState & Paramters) => {
	const {goodsCd, availEvent, ...overlayProps} = props;

	const goodsFetch = useGoodsDetail(goodsCd);
	const goods = goodsFetch.data?.body;

	if (goodsFetch.isFetching) return <></>;

	return (
		<>
			<PopupContainer {...overlayProps} size="full" title="출발일 보기">
				<style.ContentWrap>
					<EventInfo {...goods} />
					<EventContents goods={goods} availEvent={availEvent} onClose={overlayProps.close} />
				</style.ContentWrap>
			</PopupContainer>
		</>
	);
};

// 출발일 보기
const EventListPopup = (props: OverlayState & Paramters) => (
	<ErrorBoundary logMsg="EventListPop Error">
		<EventListPopComponent {...props} />
	</ErrorBoundary>
);

export default EventListPopup;
export type {EventExt, FilterType, SortType};
